import {
    ProjectConfiguration,
    ProjectGraph,
    ProjectGraphDependency,
    ProjectGraphProjectNode,
} from '@nx/devkit';
import {
    Components,
    DepSummary,
    DomainSummary,
    emptyProjectInfo,
    emptyProjectQuery,
    emptyProjectSummary,
    EnvironmentKeys,
    EnvSummary,
    LibrarySummary,
    LibraryTypes,
    ProjectInfo,
    ProjectQuery,
    ProjectSummary,
    Teams,
    TeamSummary,
} from './status-project.models';
import { isEqual, isEmpty, pick } from 'lodash';

type FileInfoKeys =
    | 'key'
    | 'team'
    | 'domain'
    | 'type'
    | 'critical'
    | 'nprod'
    | 'staging'
    | 'prod'
    | 'notUsed'
    | 'hasTags'
    | 'hasNonCriticalDeps'
    | 'proj';
const fileInfo: FileInfoKeys[] = [
    'key',
    'team',
    'domain',
    'type',
    'critical',
    'nprod',
    'staging',
    'prod',
    'notUsed',
    'hasTags',
    'hasNonCriticalDeps',
    'proj',
];

export function getProjectInfo(
    project: string,
    depGraph: ProjectGraph,
): ProjectInfo {
    if (!project?.length || !depGraph.nodes[project]) return emptyProjectInfo;

    const projectNode: ProjectGraphProjectNode = depGraph.nodes[project];
    const projectDeps: Record<string, Partial<ProjectInfo>> = getProjectDeps(
        project,
        depGraph,
    );
    const projectJson: ProjectConfiguration = projectNode?.data;
    const tagInfo: Partial<ProjectInfo> = createTagMap(projectJson?.tags);
    const hasNonCriticalDeps: boolean = Object.values(projectDeps).some(
        (dep) => !dep.critical,
    );

    return {
        ...emptyProjectInfo,
        ...tagInfo,
        key: project,
        hasTags: !!projectJson.tags.length,
        // For debugging
        // node: projectNode,
        deps: projectDeps,
        hasNonCriticalDeps,
    };
}

export function getProjectDeps(
    project: string,
    depGraph: ProjectGraph,
): Record<string, Partial<ProjectInfo>> {
    if (!project?.length) return {};
    const projectDeps: ProjectGraphDependency[] =
        depGraph.dependencies[project];

    return projectDeps?.reduce(
        (
            depMap: Record<string, Partial<ProjectInfo>>,
            dep: ProjectGraphDependency,
        ): Record<string, Partial<ProjectInfo>> => {
            const depNode: ProjectGraphProjectNode = depGraph.nodes[dep.target];
            if (!depNode) return depMap;

            const projectJson: ProjectConfiguration = depNode.data;
            const tagInfo: Partial<ProjectInfo> = createTagMap(
                projectJson.tags,
            );

            return {
                ...depMap,
                [dep.target]: {
                    ...emptyProjectInfo,
                    ...tagInfo,
                    key: dep.target,
                },
            };
        },
        {},
    );
}

export function queryProjects(
    query: ProjectQuery,
    depGraph: ProjectGraph,
): ProjectInfo[] {
    return queryProjectKeys(query, depGraph).map((project) =>
        getProjectInfo(project, depGraph),
    );
}

export function createTagMap(tags: string[]): Partial<ProjectInfo> {
    return tags?.reduce((acc: Partial<ProjectInfo>, tag: string) => {
        const [key, value]: string[] = tag.split(':');

        return {
            ...acc,
            [key]: getValue(key, value),
        };
    }, {});
}

export function getValue(key: string, value: string) {
    return {}[key] ?? (value === 'true' || (value !== 'false' && value));
}

export function updateProjectResponse(
    projectMap: Record<string, ProjectInfo>,
    project: string,
    depGraph: ProjectGraph,
): Record<string, ProjectInfo> {
    return {
        ...projectMap,
        [project]: getProjectInfo(project, depGraph),
    };
}

export function exportProjects(
    fileType: string,
    query: ProjectQuery,
    depGraph: ProjectGraph,
): string {
    const currentProjects: ProjectInfo[] = queryProjects(query, depGraph);
    const mappers: Record<string, (projects: ProjectInfo[]) => string> = {
        json: (projects: ProjectInfo[]): string => mapToJsonString(projects),
        csv: (projects: ProjectInfo[]): string => mapToCsvString(projects),
    };

    return mappers[fileType]?.(currentProjects) ?? '{}';
}

function mapToJsonString(projects: ProjectInfo[]): string {
    const allProjectInfos: Record<
        string,
        Partial<ProjectInfo>
    > = projects?.reduce(
        (acc, projectInfo) => ({
            ...acc,
            [projectInfo.key]: getFileProjectInfo(projectInfo),
        }),
        {},
    );

    try {
        return JSON.stringify(allProjectInfos);
    } catch (e) {
        console.error('Error exporting projects to json', e);
        return '{}';
    }
}

function getFileProjectInfo(projectInfo: ProjectInfo): Partial<ProjectInfo> {
    return pick(projectInfo, fileInfo) as Partial<ProjectInfo>;
}

function mapToCsvString(projects: ProjectInfo[]): string {
    const headerMap: Record<FileInfoKeys, string> = {
        proj: 'Project',
        team: 'Team',
        domain: 'Domain',
        type: 'Type',
        critical: 'Critical',
        nprod: 'NPROD',
        staging: 'Staging',
        prod: 'Prod',
        notUsed: 'Not Used',
        hasTags: 'Has Tags',
        hasNonCriticalDeps: 'Has Non-Critical Deps',
        key: 'Key',
    };
    const columnOrder: FileInfoKeys[] = [
        'proj',
        'type',
        'domain',
        'team',
        'nprod',
        'staging',
        'prod',
        'notUsed',
        'hasTags',
        'critical',
        'hasNonCriticalDeps',
        'key',
    ];
    const allProjectInfos: Partial<ProjectInfo>[] = projects?.map(
        (projectInfo: ProjectInfo): Partial<ProjectInfo> =>
            getFileProjectInfo(projectInfo),
    );

    try {
        return Object.values(allProjectInfos).reduce(
            (csv: string, projectInfo: Partial<ProjectInfo>) => {
                return `${csv} 
${columnOrder.map((key) => projectInfo[key]).join(',')}`;
            },
            columnOrder.map((key) => headerMap[key]).join(','),
        );
    } catch (e) {
        console.error('Error exporting projects to json', e);
        return '{}';
    }
}

export function mapProjectsToProjectInfo(depGraph: ProjectGraph) {
    const projects: string[] = Object.keys(depGraph.nodes);

    return projects?.reduce(
        (
            projectMap: Record<string, ProjectInfo>,
            project: string,
        ): Record<string, ProjectInfo> =>
            updateProjectResponse(projectMap, project, depGraph),
        {},
    );
}

export function mapSummary(
    query: ProjectQuery,
    depGraph: ProjectGraph,
): ProjectSummary {
    const projectKeys: string[] = queryProjectKeys(query, depGraph);
    const projects: number = projectKeys.length;
    const teams: Record<Teams, TeamSummary> = mapTeamSummary(
        projectKeys,
        depGraph,
    );
    const deps: DepSummary = mapDepSummary(projectKeys, depGraph);
    const domains: Record<Components, DomainSummary> = mapDomainSummary(
        projectKeys,
        depGraph,
    );
    const libraryTypes: Record<LibraryTypes, LibrarySummary> =
        mapLibrarySummary(projectKeys, depGraph);
    const env: Record<EnvironmentKeys, EnvSummary> = mapEnvSummary(
        projectKeys,
        depGraph,
    );

    return {
        ...emptyProjectSummary,
        projects,
        teams,
        deps,
        domains,
        libraryTypes,
        env,
    };
}

function mapEnvSummary(
    projectKeys: string[],
    depGraph: ProjectGraph,
): Record<EnvironmentKeys, TeamSummary> {
    const projectInfo: Record<string, ProjectInfo> =
        mapProjectsToProjectInfo(depGraph);
    const env: Record<EnvironmentKeys, EnvSummary> = projectKeys?.reduce(
        (
            currentEnv: Record<EnvironmentKeys, EnvSummary>,
            projectKey: string,
        ) => {
            const project: ProjectInfo = projectInfo[projectKey];
            const nprod: boolean = project.nprod;
            const staging: boolean = project.staging;
            const prod: boolean = project.prod;
            const notUsed: boolean = project.notUsed;
            const key: EnvironmentKeys = prod
                ? 'prod'
                : staging
                ? 'staging'
                : nprod
                ? 'nprod'
                : notUsed
                ? 'notUsed'
                : 'Unknown';
            const prevEnv: EnvSummary = currentEnv[key] ?? {
                total: 1,
                percentage: 1,
            };

            return {
                ...currentEnv,
                [key]: {
                    total: prevEnv.total + 1,
                    percentage: (prevEnv.total / projectKeys.length) * 100,
                },
            };
        },

        emptyProjectSummary.env,
    );

    return {
        ...emptyProjectSummary.env,
        ...env,
    };
}
function mapTeamSummary(
    projectKeys: string[],
    depGraph: ProjectGraph,
): Record<Teams, TeamSummary> {
    const projectInfo: Record<string, ProjectInfo> =
        mapProjectsToProjectInfo(depGraph);
    const teams: Record<Teams, TeamSummary> = projectKeys?.reduce(
        (
            currentTeams: Record<Teams, TeamSummary>,
            projectKey: string,
        ): Record<Teams, TeamSummary> => {
            const project: ProjectInfo = projectInfo[projectKey];
            const team: Teams = project.team as Teams;
            const currentTeam: TeamSummary = currentTeams[team] ?? {
                total: 1,
                percentage: 1,
            };

            return {
                ...currentTeams,
                [team]: {
                    total: currentTeam.total + 1,
                    percentage: (currentTeam.total / projectKeys.length) * 100,
                },
            };
        },
        emptyProjectSummary.teams,
    );

    return {
        ...emptyProjectSummary.teams,
        ...teams,
    };
}
function mapDepSummary(
    projectKeys: string[],
    depGraph: ProjectGraph,
): DepSummary {
    const projectInfo: Record<string, ProjectInfo> =
        mapProjectsToProjectInfo(depGraph);
    const deps: DepSummary = projectKeys?.reduce(
        (currentSummary: DepSummary, projectKey: string): DepSummary => {
            const project: ProjectInfo = projectInfo[projectKey];
            const isCritical: boolean = project.critical;
            const nonCritical: number = !isCritical
                ? currentSummary.nonCritical + 1
                : currentSummary.nonCritical;
            const critical: number = isCritical
                ? currentSummary.critical + 1
                : currentSummary.critical;
            const criticalPercentage: number =
                (critical / projectKeys.length) * 100;
            const nonCriticalPercentage: number =
                (nonCritical / projectKeys.length) * 100;

            return {
                ...currentSummary,
                nonCritical,
                critical,
                criticalPercentage,
                nonCriticalPercentage,
            };
        },
        emptyProjectSummary.deps,
    );

    return {
        ...emptyProjectSummary.deps,
        ...deps,
        total: projectKeys.length,
    };
}
function mapDomainSummary(
    projectKeys: string[],
    depGraph: ProjectGraph,
): Record<Components, DomainSummary> {
    const projectInfo: Record<string, ProjectInfo> =
        mapProjectsToProjectInfo(depGraph);
    const domain: Record<Components, DomainSummary> = projectKeys?.reduce(
        (
            currentDomain: Record<Components, DomainSummary>,
            projectKey: string,
        ): Record<Components, DomainSummary> => {
            const project: ProjectInfo = projectInfo[projectKey];
            const component: Components = project.domain as Components;
            const currentDomainSummary: DomainSummary = currentDomain[
                component
            ] ?? {
                total: 1,
                percentage: 1,
            };

            return {
                ...currentDomain,
                [component]: {
                    total: currentDomainSummary.total + 1,
                    percentage:
                        (currentDomainSummary.total / projectKeys.length) * 100,
                },
            };
        },
        emptyProjectSummary.domains,
    );

    return {
        ...emptyProjectSummary.domains,
        ...domain,
    };
}

function mapLibrarySummary(
    projectKeys: string[],
    depGraph: ProjectGraph,
): Record<LibraryTypes, LibrarySummary> {
    const projectInfo: Record<string, ProjectInfo> =
        mapProjectsToProjectInfo(depGraph);
    const library: Record<LibraryTypes, LibrarySummary> = projectKeys?.reduce(
        (
            currentLibrary: Record<LibraryTypes, LibrarySummary>,
            projectKey: string,
        ): Record<LibraryTypes, LibrarySummary> => {
            const project: ProjectInfo = projectInfo[projectKey];
            const libraryType: LibraryTypes = project.type as LibraryTypes;
            const currentLibrarySummary: LibrarySummary = currentLibrary[
                libraryType
            ] ?? {
                total: 1,
                percentage: 1,
            };

            return {
                ...currentLibrary,
                [libraryType]: {
                    total: currentLibrarySummary.total + 1,
                    percentage:
                        (currentLibrarySummary.total / projectKeys.length) *
                        100,
                },
            };
        },
        emptyProjectSummary.libraryTypes,
    );

    return {
        ...emptyProjectSummary.libraryTypes,
        ...library,
    };
}

export function queryProjectKeys(
    query: ProjectQuery,
    depGraph: ProjectGraph,
): string[] {
    const projectInfo: Record<string, ProjectInfo> =
        mapProjectsToProjectInfo(depGraph);

    if (isEmpty(query) || isEqual(query, emptyProjectQuery))
        return Object.keys(projectInfo);

    if (!!query?.keys?.length) return query.keys;

    return Object.entries(projectInfo)
        .filter(([key, project]: [string, ProjectInfo]) => {
            return !key.includes('mfe-');
        })
        .filter(([key, project]: [string, ProjectInfo]) => {
            const hasTeam = !!query?.teams?.length
                ? query.teams?.includes(project.team as Teams)
                : true;
            const hasDomain = !!query?.domains?.length
                ? query.domains?.includes(project.domain as Components)
                : true;
            const hasType = !!query?.types?.length
                ? query.types?.includes(project.type as LibraryTypes)
                : true;
            const hasEnv = !!query?.envs?.length
                ? query.envs?.includes(getMaxEnv(key, projectInfo))
                : true;
            const critical = query.critical ? project.critical : true;

            return hasTeam && hasDomain && hasType && hasEnv && critical;
        })
        .map(([key, project]) => key);
}

function getMaxEnv(
    projectKey: string,
    projectInfo: Record<string, ProjectInfo>,
): EnvironmentKeys {
    const project: ProjectInfo = projectInfo[projectKey];
    const nprod: boolean = project.nprod;
    const staging: boolean = project.staging;
    const prod: boolean = project.prod;
    const notUsed: boolean = project.notUsed;
    const key: EnvironmentKeys = prod
        ? 'prod'
        : staging
        ? 'staging'
        : nprod
        ? 'nprod'
        : notUsed
        ? 'notUsed'
        : 'Unknown';

    return key;
}
